import React from 'react';

import { useCurrentTheme } from 'app/shared/theme';
import { useIsBreakpointSize } from 'app/shared/utils/useBreakpoint';
import { useEventHeaderImageSourceMap } from 'app/shared/utils/useEventHeaderImageSourceMap';
import { AttendeeState, Event } from 'app/shared/typings';
import { Image, LoadingBlock, VisaLogo } from 'app/shared/components/atoms';
import { Grid, GridItem } from 'app/shared/components/atoms';
import { useEventAndAttendeeStatusMessaging } from 'app/fan/utils';
import { EventTags, ItemTag, StatusBar } from 'app/fan/components/molecules';

interface EventHeaderImageProps {
  event?: Event;
  attendeeState?: AttendeeState;
  numTickets?: number;
}

export const EventHeaderImage: React.FC<
  React.PropsWithChildren<EventHeaderImageProps>
> = ({ event, attendeeState, numTickets }) => {
  const eventHeaderImageInfo = useEventHeaderImageSourceMap({ event });
  const { colors, fontSizes, ruler } = useCurrentTheme();
  const { isDesktop } = useIsBreakpointSize();
  const visaLogoHeight = isDesktop
    ? `calc(${fontSizes.fontSize50.desktop} + ${ruler[1]}px)`
    : `calc(${fontSizes.fontSize50.mobile} + ${ruler[1]}px)`;

  const { statusMessage, messageType, grayscale } =
    useEventAndAttendeeStatusMessaging({
      event,
      attendeeState,
      numTickets,
    });

  if (!eventHeaderImageInfo || !event) {
    return <LoadingBlock h="100%" w="100%" aspectRatio="16/9" />;
  }

  const gridItemProps = {
    row: 1,
    mobile: 6,
    mobileStart: 1,
    tabletStart: 1,
    smallDesktopStart: 1,
    largeDesktopStart: 1,
  };

  return (
    <Grid px={0} numberOfColumns={6} gap={0}>
      <GridItem {...gridItemProps}>
        <EventTags
          backgroundComponent={
            <Image
              h="inherit"
              grayscale={grayscale}
              {...eventHeaderImageInfo.imageSourceMap}
              data-qaid="event-page-header-image"
            />
          }
          w="inherit"
          h="inherit"
          pl={[4, 8, 8, 8]}
        >
          {event.onPresale && (
            <VisaLogo fill={colors.primary.white.lightest} h={visaLogoHeight} />
          )}
          {event.theme && (
            <ItemTag
              tag={event.theme?.title}
              backgroundColor={colors.primary.white.base}
              px={2}
              py={1}
            />
          )}
        </EventTags>
      </GridItem>
      <GridItem alignSelf="end" {...gridItemProps} zIndex={1}>
        <StatusBar
          data-qaid="event-page-status-bar"
          statusMessage={statusMessage}
          messageType={messageType}
        />
      </GridItem>
    </Grid>
  );
};
